import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';

import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { Countries } from '../../../../constants';

const buttonProps: ButtonProps = {
  style: { fontWeight: 600, padding: '6px 40px', marginLeft: '15px' },
  variant: 'contained',
};

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

// DATA
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === timezone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

const isEmailValid = (email: string) => {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email.toLocaleLowerCase().match(regex);
};

export interface ILeadGenState {
  email: string;
  fullName: string;
  phoneNumber: string;
  country: string;
}

interface LeadGenModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (values: ILeadGenState) => void;
}

const initialState: ILeadGenState = {
  email: '',
  fullName: '',
  phoneNumber: '',
  country: timezone,
};

const defaultErrorData = {
  visible: false,
  message: '',
};

const LeadGenModal: React.FC<LeadGenModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  // State
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({
    fullName: { ...defaultErrorData },
    email: { ...defaultErrorData },
    phoneNumber: { ...defaultErrorData },
  });

  useEffect(() => {
    if (state.fullName) {
      let _nameError = { ...defaultErrorData };

      if (state.fullName.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        fullName: _nameError,
      }));
    }
  }, [state.fullName]);

  useEffect(() => {
    if (state.email) {
      let _emailError = { ...defaultErrorData };

      if (!isEmailValid(state.email)) {
        _emailError = {
          visible: true,
          message: 'Invalid Email',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        email: _emailError,
      }));
    }
  }, [state.email]);

  useEffect(() => {
    if (state.phoneNumber) {
      let _nameError = { ...defaultErrorData };

      if (state.phoneNumber.length < 9) {
        _nameError = {
          visible: true,
          message: 'Phone Number should have at least 9 numbers',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        phoneNumber: _nameError,
      }));
    }
  }, [state.phoneNumber]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const submitButtonDisabled =
    state.phoneNumber.trim().length < 9 ||
    state.fullName.trim().length < 3 ||
    state.email.length === 0 ||
    !isEmailValid(state.email);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '550px',
        },
      }}
    >
      <DialogTitle fontWeight="600" align="center">
        Elevate Your Learning Experience
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          fontSize="12px"
          fontWeight="600"
          align="center"
          marginBottom="30px"
        >
          You are one step closer to starting your learning journey with us.
          Fill in the details below
        </DialogContentText>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <FormControl fullWidth>
              <Typography {...label}>Full Name *</Typography>
              <TextField
                size="small"
                name="fullName"
                type="text"
                placeholder="Type your full name"
                onChange={handleInputChange}
                error={errors.fullName.visible}
                helperText={errors.fullName.message || ' '}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <FormControl fullWidth>
              <Typography {...label}>Email *</Typography>
              <TextField
                size="small"
                name="email"
                type="email"
                placeholder="example@gmail.com"
                onChange={handleInputChange}
                error={errors.email.visible}
                helperText={errors.email.message || ' '}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Typography {...label}>Phone Number *</Typography>
            <PhoneInput
              international
              withCountryCallingCode
              defaultCountry={defaultCountryCode}
              placeholder="Enter phone number"
              value={state.phoneNumber}
              onChange={(value) => {
                if (value) {
                  setState((prevState) => ({
                    ...prevState,
                    phoneNumber: value,
                  }));
                }
              }}
              className="phone-number-input-custom-styles"
            />
            <FormHelperText style={{ marginLeft: '14px', color: '#FF2B2D' }}>
              {errors.phoneNumber.message || ' '}
            </FormHelperText>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '24px' }}>
        <Button {...buttonProps} color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          {...buttonProps}
          color="primary"
          disabled={submitButtonDisabled}
          onClick={() => onConfirm(state)}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadGenModal;
